import React, { useEffect, useState } from 'react';

import bumpIcon from '../images/ic_bump.png';
import superBumpIcon from '../images/ic_super_bump.png';

import api from '../utils/Api';
import { trackCheckoutStarted } from '../utils/Analytics';

import './BuyBumps.scss';

export interface BumpPack {
  productId: string;
  name: string;
  amount: number;
  currency: string;
  bumps: number;
  superBumps: number;
}

interface BuyBumpsProps {
  token: string;
  // The page Stripe sends the buyer back to, such as /jukebox/abc123.
  returnPath: string;
  // The jukebox this is bought on, so its venue gets its share of the sale.
  playerIdentifier?: string;
  onError: (message: string) => void;
}

export const formatPrice = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
  } catch (error) {
    return `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
};

/**
 * Buying bumps on the web. Stripe hosts the payment page: choosing a pack asks the server
 * for a Checkout link and goes there. The bumps are added by the server once Stripe tells
 * it the payment went through, never by this page, so coming back here only refreshes the
 * counts.
 *
 * Shows nothing at all when the server has no Stripe keys.
 */
const BuyBumps = (props: BuyBumpsProps) => {
  const { token, returnPath, playerIdentifier, onError } = props;
  const [packs, setPacks] = useState<BumpPack[]>([]);
  const [open, setOpen] = useState(false);
  const [buying, setBuying] = useState<string>(null);

  useEffect(() => {
    api
      .users('bumpPacks', 'GET', token, null)
      .then(response => setPacks((response && response.packs) || []))
      .catch(() => setPacks([]));
  }, [token]);

  if (packs.length === 0) {
    return null;
  }

  const buy = async (pack: BumpPack) => {
    setBuying(pack.productId);
    try {
      const { url } = await api.users('buyBumps', 'POST', token, {
        productId: pack.productId,
        returnPath,
        playerIdentifier,
      });
      trackCheckoutStarted(pack, playerIdentifier);
      window.location.assign(url);
    } catch (error) {
      setBuying(null);
      onError(error.message);
    }
  };

  return (
    <div className="buy-bumps">
      <button
        type="button"
        className={open ? 'buy-bumps-toggle is-open' : 'buy-bumps-toggle'}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {open ? 'Close' : 'Buy bumps'}
      </button>

      {open && (
        <ul className="buy-bumps-list">
          {packs.map(pack => (
            <li key={pack.productId} className={pack.superBumps ? 'buy-bumps-pack is-super' : 'buy-bumps-pack'}>
              <img src={pack.superBumps ? superBumpIcon : bumpIcon} alt="" />
              <span className="buy-bumps-name">{pack.name}</span>
              <span className="buy-bumps-price">{formatPrice(pack.amount, pack.currency)}</span>
              <button type="button" disabled={!!buying} onClick={() => buy(pack)}>
                {buying === pack.productId ? 'Opening' : 'Buy'}
              </button>
            </li>
          ))}
          <li className="buy-bumps-small">Payment is handled by Stripe. Bumps are added to this account.</li>
        </ul>
      )}
    </div>
  );
};

export default BuyBumps;
